import React, { useState, useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import AdxInput from 'components/custom/AdxInput'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import AddIcon from '@mui/icons-material/Add'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import AdxStatusBar from 'components/reusable/statusBar/AdxStatusBar'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'

import { Formik, Form, ErrorMessage, Field } from 'formik'

import * as Yup from 'yup'

import { getApproverGroup } from 'services/api/admin-accessibility'
import { postApprovalRoute, putApprovalRoute } from 'services/api/approval-routing'
import { handleValidationErrors } from 'services/helper/handleValidationErrors'

import StepperAvatarDropDown from './StepperAvatarDropDown'

import { Icon } from '@mui/material'

import { useQuery, useMutation, useQueryClient } from 'react-query'
import AdxButton from 'components/custom/AdxButton'

const defaultInitialValues = {
  name: '',
  layer_one: '',
}

const layerFields = ['layer_one', 'layer_two', 'layer_three', 'layer_four', 'layer_five']

const buildApproverList = (count = 1) =>
  layerFields.slice(0, Math.max(count, 1)).map((nameWord, index) => ({
    nameWord,
    nameNumber: `Step ${index + 1}`,
  }))

const AddNewApprovalRouteModal = ({
  showAddNewApprovalRouteModal,
  handleCloseAddNewApprovalRouteModal,
  selectedCardId,
  selectedCard,
}) => {
  const formikRef = useRef()

  const [selectValue1, setSelectValue1] = useState('')
  const [selectValue2, setSelectValue2] = useState('')
  const [selectValue3, setSelectValue3] = useState('')
  const [selectValue4, setSelectValue4] = useState('')
  const [selectValue5, setSelectValue5] = useState('')
  const [initialValues, setInitialValues] = useState(defaultInitialValues)

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(),
  })

  const [approverList, setApproverList] = useState(buildApproverList())

  const handleSetSelectValue = (event, index) => {
    const { id } = event.target.value
    switch (index) {
      case 0:
        setSelectValue1(event.target.value)
        formikRef.current.setFieldValue('layer_one', id)
        break
      case 1:
        setSelectValue2(event.target.value)
        formikRef.current.setFieldValue('layer_two', id)
        break
      case 2:
        setSelectValue3(event.target.value)
        formikRef.current.setFieldValue('layer_three', id)
        break
      case 3:
        setSelectValue4(event.target.value)
        formikRef.current.setFieldValue('layer_four', id)
        break
      case 4:
        setSelectValue5(event.target.value)
        formikRef.current.setFieldValue('layer_five', id)
        break
      default:
        setSelectValue1(event.target.value)
        formikRef.current.setFieldValue('layer_one', id)
    }
  }

  const handleValue = (index) => {
    switch (index) {
      case 0:
        return selectValue1
      case 1:
        return selectValue2
      case 2:
        return selectValue3
      case 3:
        return selectValue4
      case 4:
        return selectValue5
      default:
        return selectValue1
    }
  }

  const approverGroupList = useQuery('approverGroupList', async () => {
    const { data } = await getApproverGroup()
    return data
  })

  const handleAddLayer = (layer) => {
    let newApprover

    switch (layer) {
      case 1:
        newApprover = {
          nameWord: 'layer_one',
          nameNumber: 'Step 1',
        }
        formikRef.current.setFieldValue('layer_one', '')
        break
      case 2:
        newApprover = {
          nameWord: 'layer_two',
          nameNumber: 'Step 2',
        }
        formikRef.current.setFieldValue('layer_two', '')
        break
      case 3:
        newApprover = {
          nameWord: 'layer_three',
          nameNumber: 'Step 3',
        }
        formikRef.current.setFieldValue('layer_three', '')
        break
      case 4:
        newApprover = {
          nameWord: 'layer_four',
          nameNumber: 'Step 4',
        }
        formikRef.current.setFieldValue('layer_four', '')
        break
      case 5:
        newApprover = {
          nameWord: 'layer_five',
          nameNumber: 'Step 5',
        }
        formikRef.current.setFieldValue('layer_five', '')
        break

      default:
        newApprover = {
          nameWord: 'layer_one',
          nameNumber: 'Step 1',
        }
    }
    setApproverList([...approverList, newApprover])
  }

  const handleDeleteLayer = () => {
    let newApproverList = [...approverList]
    newApproverList.pop()
    setApproverList(newApproverList)
    switch (newApproverList.length) {
      case 0:
        const { layer_one, ...rest1 } = formikRef.current.values
        formikRef.current.setValues(rest1)
        setSelectValue1('')
        break
      case 1:
        const { layer_two, ...rest2 } = formikRef.current.values
        formikRef.current.setValues(rest2)
        setSelectValue2('')
        break
      case 2:
        const { layer_three, ...rest3 } = formikRef.current.values
        formikRef.current.setValues(rest3)
        setSelectValue3('')
        break
      case 3:
        const { layer_four, ...rest4 } = formikRef.current.values
        formikRef.current.setValues(rest4)
        setSelectValue4('')
        break
      case 4:
        const { layer_five, ...rest5 } = formikRef.current.values
        formikRef.current.setValues(rest5)
        setSelectValue5('')
        break
      default:
        formikRef.current.setValues(formikRef.current.values)
        setSelectValue1('')
    }
  }

  const queryClient = useQueryClient()

  const mutate = useMutation(
    async (data) => {
      if (selectedCardId) {
        await putApprovalRoute(selectedCardId, data)
      } else {
        await postApprovalRoute(data)
      }
    },
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries('allApprovalRouteList')
        handleCloseModal()
      },
      onError: (e) => {
        if (e.status === 422) {
          handleValidationErrors(e, formikRef)
        } else {
          console.log(e.data.message)
        }
      },
    }
  )

  const handleOnSubmit = (values, actions) => {
    mutate.mutate(values)
  }

  const handleCloseModal = () => {
    handleCloseAddNewApprovalRouteModal()
    setInitialValues(defaultInitialValues)
    setApproverList(buildApproverList())
    setSelectValue1('')
    setSelectValue2('')
    setSelectValue3('')
    setSelectValue4('')
    setSelectValue5('')
  }

  useEffect(() => {
    if (selectedCardId) {
      const selectedLayers = layerFields.map((field) => selectedCard[field]).filter(Boolean)

      setApproverList(buildApproverList(selectedLayers.length))

      const data = {
        name: selectedCard.name || '',
        layer_one: selectedCard.layer_one?.id || '',
        layer_two: selectedCard.layer_two?.id || '',
        layer_three: selectedCard.layer_three?.id || '',
        layer_four: selectedCard.layer_four?.id || '',
        layer_five: selectedCard.layer_five?.id || '',
      }
      setInitialValues(data)

      setSelectValue1(selectedCard.layer_one || '')
      setSelectValue2(selectedCard.layer_two || '')
      setSelectValue3(selectedCard.layer_three || '')
      setSelectValue4(selectedCard.layer_four || '')
      setSelectValue5(selectedCard.layer_five || '')
    } else {
      setInitialValues(defaultInitialValues)
      setApproverList(buildApproverList())
      setSelectValue1('')
      setSelectValue2('')
      setSelectValue3('')
      setSelectValue4('')
      setSelectValue5('')
    }
  }, [selectedCardId, selectedCard])

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      innerRef={formikRef}
      onSubmit={(values, actions) => handleOnSubmit(values, actions)}
      validationSchema={validationSchema}
    >
      {({ values, errors, touched, handleSubmit, handleChange, handleBlur }) => (
        <Form>
          <AdxFormModal
            title={selectedCardId ? 'Edit approval flow' : 'Add approval flow'}
            icon={<Icon>add</Icon>}
            open={showAddNewApprovalRouteModal}
            onClose={handleCloseModal}
            handleClose={handleCloseModal}
            childPadding={false}
            enableFooter
            actionText={selectedCardId ? 'Save flow' : 'Create flow'}
            handleAction={handleSubmit}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 3, pb: 2 }}>
              <Box sx={{ display: `flex`, flexDirection: `column`, gap: 2 }}>
                <Field
                  as={AdxInput}
                  name="name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Approval flow name"
                  size="standard"
                  placeholder="Example: Direct Manager > HR Admin"
                  charLimit={50}
                  sx={{ width: '100%' }}
                  errorMessage={<ErrorMessage name="name" component={AdxErrorMessage} />}
                  isErrorShown={errors.name && touched.name}
                />
                <Box border="1px solid" borderColor="gray-line.main" borderRadius={`4.5px`}>
                  {approverList.map((item, index) => (
                    <Box key={index.toString()}>
                      <Box px={2} py={2} display="flex" gap={2} alignItems="center">
                        <Box width={'100px'}>
                          <Typography>{item.nameNumber}</Typography>
                        </Box>
                        <Box width="calc(100% - 116px)">
                          <Field
                            as={AdxDropdown}
                            name={item.nameWord}
                            IconComponent={KeyboardArrowDownIcon}
                            sx={{
                              width: '100%',
                              backgroundColor: 'gray-background.main',
                              borderRadius: `4.5px`,
                              '& .MuiOutlinedInput-root': {
                                height: 'fit-content',
                              },
                            }}
                            value={handleValue(index)}
                            onChange={(e) => handleSetSelectValue(e, index)}
                            isErrorShown={errors[item.nameWord] && touched[item.nameWord]}
                            errorMessage={
                              <ErrorMessage name={item.nameWord} component={AdxErrorMessage} />
                            }
                          >
                            {/* <pre>{JSON.stringify({data:[handleValue(index), ...approverGroupList?.data].filter(x => x !== "")}, null,4)}</pre> */}
                            {[
                              handleValue(index),
                              ...(approverGroupList?.data || []).filter(
                                (item) => item?.id !== handleValue(index)?.id || !handleValue(index)
                              ),
                            ]
                              ?.filter((x) => x !== '')
                              .map?.((el, idx) => (
                                <AdxMenuItem
                                  value={el}
                                  selectedvalue={handleValue(index)}
                                  key={idx.toString()}
                                >
                                  <Box display="flex" alignItems="center" gap={2}>
                                    <Box sx={{ position: `relative` }} width={70} height={70}>
                                      <StepperAvatarDropDown el={el} />
                                    </Box>
                                    <Box
                                      display="flex"
                                      gap={1}
                                      flexDirection={'column'}
                                      justifyContent="center"
                                      width={'90%'}
                                      maxWidth={500}
                                    >
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="body1">
                                          {el?.group_name || el?.name}
                                        </Typography>
                                        {el?.auto_generated ? (
                                          <AdxStatusBar status="AUTO-GENERATED" />
                                        ) : null}
                                      </Box>
                                      <Typography variant="body2" sx={{ whiteSpace: `wrap` }}>
                                        {el?.members
                                          .map((item) => item?.name)
                                          .toString()
                                          .replaceAll(',', ' , ')}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </AdxMenuItem>
                              ))}
                          </Field>
                        </Box>
                        {approverList.length === index + 1 && approverList.length !== 1 ? (
                          <IconButton onClick={() => handleDeleteLayer()} sx={{ p: 0 }}>
                            <RemoveCircleIcon color="error" />
                          </IconButton>
                        ) : (
                          <Box width="20px" height="20px" />
                        )}
                      </Box>

                      <Divider />
                    </Box>
                  ))}
                  {approverList.length < 5 && (
                    <AdxButton
                      variant={'ghost'}
                      onClick={() => handleAddLayer(approverList.length + 1)}
                      sx={{ display: 'flex', gap: 1, width: 1 }}
                    >
                      <AddIcon />
                      Add approval step
                    </AdxButton>
                  )}
                </Box>
              </Box>
              <Box>
                {!(
                  values.layer_one === 3 ||
                  values.layer_two === 3 ||
                  values.layer_three === 3 ||
                  values.layer_four === 3 ||
                  values.layer_five === 3
                ) && (
                  <Box sx={{ display: `flex`, flexDirection: `column`, gap: 1 }}>
                    <Typography variant="body1">Backup flow</Typography>
                    <Typography variant="subtitle1" color="light-gray-text.main">
                      If the request skips all approval steps, the Direct Reporting Manager will be
                      called automatically to act as the backup approver.{' '}
                    </Typography>
                    <Box
                      border="1px solid"
                      borderColor="gray-line.main"
                      p={2}
                      borderRadius={`4.5px`}
                      display="flex"
                      alignItems="center"
                      gap={2}
                    >
                      <Box position="relative" width={60} height={60}>
                        <StepperAvatarDropDown
                          el={approverGroupList?.data?.find((el) => el?.id === 3)}
                        />
                      </Box>
                      <Box
                        display="flex"
                        gap={1}
                        flexDirection={'column'}
                        justifyContent="center"
                        width={'90%'}
                      >
                        <Typography variant="body1">Direct reporting manager</Typography>
                        <Typography variant="body2">
                          {approverGroupList?.data
                            ?.find((el) => el?.id === 3)
                            .members?.map((item) => item?.name)
                            .toString()
                            .replaceAll(',', ' , ')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </AdxFormModal>
        </Form>
      )}
    </Formik>
  )
}

export default AddNewApprovalRouteModal
